/** Engine imports keep stable object addresses; private Storage needs a fresh read URL at display time. */
const ORIGIN = 'https://jrxqksupabcvfnjxbeez.supabase.co';
const PREFIX = '/storage/v1/object/public/vicsel-exam-images/';
export function vseImagePath(src: string): string | null {
  try {
    const u = new URL(src);
    if (u.origin !== ORIGIN || !u.pathname.startsWith(PREFIX)) return null;
    const path = decodeURIComponent(u.pathname.slice(PREFIX.length));
    return /^[a-f0-9-]+\/vse\/[A-Z]{2}-\d{3}\//.test(path) && !path.split('/').includes('..') ? path : null;
  } catch { return null; }
}

export function resolveVseImages(root: HTMLElement, sign: (path: string) => Promise<string>) {
  const cache = new Map<string, { url: string; until: number }>();
  const pending = new Map<string, Promise<string>>();
  const displayed = new Map<HTMLImageElement, { original: string; signed: string }>();
  let disposed = false;
  const resolve = (path: string) => {
    const cached = cache.get(path);
    if (cached && cached.until > Date.now()) return Promise.resolve(cached.url);
    const existing = pending.get(path);
    if (existing) return existing;
    const request = sign(path).then(url => {
      cache.set(path, { url, until: Date.now() + 45 * 60_000 });
      return url;
    }).finally(() => pending.delete(path));
    pending.set(path, request);
    return request;
  };
  type Loading = { original: string; dispose: () => void };
  const loading = new Map<HTMLImageElement, Loading>();
  const visit = (img: HTMLImageElement) => {
    const src = img.getAttribute('src') || '';
    const previous = displayed.get(img);
    const original = previous?.signed === src ? previous.original : src;
    const path = vseImagePath(original);
    const active = loading.get(img);
    if (!path) { active?.dispose(); displayed.delete(img); return; }
    if (active?.original === original) return;
    active?.dispose();
    const cached = cache.get(path);
    if (src === cached?.url && cached.until > Date.now() && img.complete && img.naturalWidth > 0) return;

    // Hide the inaccessible stable source before the browser paints an error icon.
    // Keep alt text on the image; the status announces that its content is pending.
    const visibility = img.style.visibility;
    img.style.visibility = 'hidden';
    const status = document.createElement('span');
    status.className = 'block text-sm text-muted-foreground py-3';
    status.setAttribute('role', 'status');
    status.textContent = 'Loading illustration…';
    img.after(status);
    let signed: string | undefined;
    const current: Loading = { original, dispose: () => {
      img.removeEventListener('load', loaded);
      img.removeEventListener('error', failedLoad);
      status.remove();
      img.style.visibility = visibility;
      if (loading.get(img) === current) loading.delete(img);
    } };
    const isCurrent = () => !disposed && img.isConnected && loading.get(img) === current;
    const loaded = () => { if (isCurrent() && img.getAttribute('src') === signed) current.dispose(); };
    const failed = () => {
      if (!isCurrent()) return;
      status.replaceChildren(document.createTextNode('Illustration unavailable. '));
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'underline font-medium';
      retry.textContent = 'Retry image';
      retry.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        cache.delete(path);
        current.dispose();
        img.setAttribute('src', original);
        visit(img);
      });
      status.append(retry);
    };
    const failedLoad = () => {
      // Ignore the public-source failure while its signed replacement is pending.
      if (signed && img.getAttribute('src') === signed) failed();
    };
    loading.set(img, current);
    img.addEventListener('load', loaded);
    img.addEventListener('error', failedLoad);
    void resolve(path).then(url => {
      if (!isCurrent() || img.getAttribute('src') !== src) return;
      signed = url;
      displayed.set(img, { original, signed: url });
      if (src !== url) img.setAttribute('src', url);
      else if (img.complete) { if (img.naturalWidth > 0) loaded(); else failed(); }
    }).catch(failed);
  };
  const scan = () => {
    for (const [img, state] of loading) if (!img.isConnected) state.dispose();
    for (const img of displayed.keys()) if (!img.isConnected) displayed.delete(img);
    root.querySelectorAll<HTMLImageElement>('img[src]').forEach(visit);
  };
  const observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') visit(record.target as HTMLImageElement);
      else record.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return;
        if (node instanceof HTMLImageElement) visit(node);
        node.querySelectorAll<HTMLImageElement>('img[src]').forEach(visit);
      });
    }
  });
  observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] });
  scan();
  const timer = window.setInterval(scan, 10 * 60_000);
  window.addEventListener('focus', scan);
  return () => { disposed = true; observer.disconnect(); window.clearInterval(timer); window.removeEventListener('focus', scan); for (const state of loading.values()) state.dispose(); displayed.clear(); cache.clear(); };
}
